import { ExclamationCircleOutlined } from '@ant-design/icons'
import React, { useCallback, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useConfirm } from 'sutra/components/confirm/use-confirm'

import { useApiClient } from 'clients/yogi-api'
import {
  fetchFilterOptionsV2,
  useFilterService,
} from 'features/filters/service'
import type { FilterOption } from 'features/filters/types'
import { useFeatureFlags } from 'features/project/hooks/use-feature-flags'
import type { FeatureFlagsResponse } from 'features/project/service/feature-flags-service'
import { useProjectStore } from 'features/project/store'
import { AgentListView } from 'features/trend-detection/features/agent-list/components/AgentListView'
import {
  ISSUE_AGENTS_QUERY_KEY,
  useAgentsList,
} from 'features/trend-detection/features/agent-list/hooks/use-agents-list'
import { useInsightAgents } from 'features/trend-detection/features/insight-agent/hooks/use-insight-agents'
import { useAgentInsightService } from 'features/trend-detection/features/insight-agent/service'
import type { AgentInsightAgent } from 'features/trend-detection/features/insight-agent/types'
import {
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import {
  trendDetectionAgentDetailPath,
  trendDetectionBenchmarkingAgentPath,
} from 'features/trend-detection/paths'
import type { TrendDetectionService } from 'features/trend-detection/service'
import type {
  AgentSummaryResponse,
  AgentType,
  BenchmarkingAgentSummary,
} from 'features/trend-detection/types'
import type { ProjectState } from 'types/project'

// Optimistic status flip for the rerun action. Both agent types (issue,
// benchmarking) hold their run under `latest_run` with a compatible shape
// (status + completed_at); everything else is spread-forwarded.
function flipAgentToRunning<
  T extends {
    uuid: string
    latest_run?: { status: string; completed_at: string | null } | null
  },
>(agents: T[] | undefined, agentUuid: string): T[] | undefined {
  return agents?.map((a) =>
    a.uuid === agentUuid && a.latest_run
      ? {
          ...a,
          latest_run: {
            ...a.latest_run,
            status: 'running',
            completed_at: null,
          },
        }
      : a,
  )
}

export interface AgentListProps {
  service: TrendDetectionService
  onSelectAgent: (agentUuid: string) => void
  /**
   * When provided, the meatballs "Edit" action for issue agents calls this
   * instead of onSelectAgent. Use to navigate directly into edit mode.
   */
  onEditAgent?: (agentUuid: string) => void
  /** Where "Create Agent" / QuickCreateCards navigate to. */
  createPath: string
}

export const AgentList: React.FC<AgentListProps> = ({
  service,
  onSelectAgent,
  onEditAgent,
  createPath,
}) => {
  const projectId = useProjectStore((state: ProjectState) => state.projectId)
  const filterService = useFilterService()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const insightAgentService = useAgentInsightService()

  const { data: featureFlagsRaw, isLoading: featureFlagsLoading } =
    useFeatureFlags()
  const featureFlags = featureFlagsRaw as FeatureFlagsResponse | undefined
  const hasBenchmarkingFlag = !!featureFlags?.['benchmarking_agent']
  const hasGeneralAgentFlag = !!featureFlags?.['general_agent']

  // Surfaced to the list's QuickCreateCards "more types" dropdown only —
  // the create page (features/trend-detection/features/agent-create) does
  // its own flag-gated computation for the in-form type field.
  const availableAgentTypes: AgentType[] = [
    'issue_detection',
    ...(hasBenchmarkingFlag ? (['benchmarking'] as const) : []),
    ...(hasGeneralAgentFlag ? (['general'] as const) : []),
  ]

  const apiClient = useApiClient()

  const { data: filterOptions } = useQuery<FilterOption[]>({
    queryKey: ['filter-options-list', projectId],
    queryFn: async () => {
      if (!projectId) return []
      return fetchFilterOptionsV2(projectId, null, filterService)
    },
    enabled: !!projectId,
    refetchOnWindowFocus: false,
    staleTime: 300000,
  })

  const agentsList = useAgentsList(service, projectId)

  const {
    data: benchmarkingAgents = [],
    isLoading: benchmarkingAgentsLoading,
  } = useQuery<BenchmarkingAgentSummary[]>({
    queryKey: ['benchmarking-agents', projectId],
    queryFn: async () => {
      if (!projectId) return []
      const { data } = await apiClient.get(
        `/benchmarking/agents?proj_uuid=${projectId}`,
      )
      return (data as BenchmarkingAgentSummary[]).map((a) => ({
        ...a,
        agent_type: 'benchmarking' as const,
      }))
    },
    enabled: !!projectId && hasBenchmarkingFlag,
    refetchOnWindowFocus: false,
    staleTime: 60000,
  })

  const { insightAgents, loadingInsightAgents, refetchInsightAgents } =
    useInsightAgents(insightAgentService, projectId, hasGeneralAgentFlag)

  // A project with zero agents of every kind bounces straight into the
  // create page rather than showing an empty table. Every agent SOURCE must
  // be counted here. Omitting one makes a project that owns only that kind
  // look agent-less, so this effect bounces the user into the create page
  // and the table they own is never shown.
  useEffect(() => {
    if (!projectId) return
    if (featureFlagsLoading) return
    if (hasBenchmarkingFlag && benchmarkingAgentsLoading) return

    const isZeroAgentsListView =
      !agentsList.loadingAgents &&
      agentsList.agents.length === 0 &&
      benchmarkingAgents.length === 0 &&
      insightAgents.length === 0

    if (!isZeroAgentsListView) return

    navigate(createPath, { replace: true })
  }, [
    projectId,
    createPath,
    hasBenchmarkingFlag,
    featureFlagsLoading,
    benchmarkingAgentsLoading,
    agentsList.loadingAgents,
    agentsList.agents.length,
    benchmarkingAgents.length,
    insightAgents.length,
    navigate,
  ])

  // Agent Center page view — fires once per mount
  const agentListViewFiredRef = useRef(false)
  useEffect(() => {
    if (!projectId) return
    if (agentsList.loadingAgents || benchmarkingAgentsLoading) return
    if (agentListViewFiredRef.current) return
    agentListViewFiredRef.current = true
    trackResearchAgents(ResearchAgentsAction.agent_list_view, {
      project_id: projectId,
      has_agents: agentsList.agents.length > 0 || benchmarkingAgents.length > 0,
    })
  }, [
    projectId,
    agentsList.loadingAgents,
    agentsList.agents.length,
    benchmarkingAgentsLoading,
    benchmarkingAgents.length,
  ])

  const handleStartSetup = useCallback(() => {
    navigate(createPath)
  }, [navigate, createPath])

  // Quick-create from the list-page cards: skips straight to /create with
  // agent_type in the URL — the create page's type field preselects from it.
  const handleQuickCreate = useCallback(
    (type: AgentType) => {
      trackResearchAgents(ResearchAgentsAction.agent_type_select, {
        agent_type: type,
      })
      navigate(`${createPath}?agent_type=${type}`)
    },
    [navigate, createPath],
  )

  const handleRerun = useCallback(
    async (
      agentUuid: string,
      agentType: 'issue_detection' | 'benchmarking',
    ) => {
      if (!projectId) return
      const queryKey =
        agentType === 'benchmarking'
          ? ['benchmarking-agents', projectId]
          : [ISSUE_AGENTS_QUERY_KEY, projectId]
      // Optimistic status flip so the row shows "Running now" immediately.
      // Retry only surfaces on rows with a failed run, so latest_run is present.
      if (agentType === 'benchmarking') {
        queryClient.setQueryData<BenchmarkingAgentSummary[] | undefined>(
          queryKey,
          (old) => flipAgentToRunning(old, agentUuid),
        )
      } else {
        queryClient.setQueryData<AgentSummaryResponse[] | undefined>(
          queryKey,
          (old) => flipAgentToRunning(old, agentUuid),
        )
      }
      try {
        if (agentType === 'benchmarking') {
          await apiClient.post(
            `/benchmarking/agents/${agentUuid}/run?proj_uuid=${projectId}`,
            {},
          )
        } else {
          await service.runAgent(projectId, agentUuid, {})
        }
        queryClient.invalidateQueries(queryKey)
        trackResearchAgents(ResearchAgentsAction.agent_run, {
          agent_type: agentType,
          agent_uuid: agentUuid,
          trigger: 'rerun_from_list',
        })
        toast.success('Rerun started')
      } catch (err) {
        // Refetch to revert the optimistic status.
        queryClient.invalidateQueries(queryKey)
        const message =
          err instanceof Error ? err.message : 'Failed to start rerun'
        toast.error(message)
      }
    },
    [projectId, apiClient, service, queryClient],
  )

  const handleToggleEnabled = useCallback(
    async (
      agentUuid: string,
      agentType: 'issue_detection' | 'benchmarking',
      newEnabled: boolean,
    ) => {
      if (!projectId) return
      try {
        if (agentType === 'benchmarking') {
          await service.setBenchmarkingAgentEnabled(
            projectId,
            agentUuid,
            newEnabled,
          )
          queryClient.setQueryData<BenchmarkingAgentSummary[] | undefined>(
            ['benchmarking-agents', projectId],
            (old) =>
              old?.map((a) =>
                a.uuid === agentUuid ? { ...a, is_enabled: newEnabled } : a,
              ),
          )
        } else {
          await service.setAgentEnabled(projectId, agentUuid, newEnabled)
          queryClient.setQueryData<AgentSummaryResponse[] | undefined>(
            [ISSUE_AGENTS_QUERY_KEY, projectId],
            (old) =>
              old?.map((a) =>
                a.uuid === agentUuid ? { ...a, is_enabled: newEnabled } : a,
              ),
          )
        }
      } catch {
        // Refetch server state so the toggle reverts via prop sync
        queryClient.invalidateQueries([ISSUE_AGENTS_QUERY_KEY, projectId])
        queryClient.invalidateQueries(['benchmarking-agents', projectId])
        toast.error('Failed to update agent')
      }
    },
    [projectId, service, queryClient],
  )

  const confirm = useConfirm()

  const handleDeleteAgent = useCallback(
    (
      agentUuid: string,
      agentType: 'issue_detection' | 'benchmarking',
      agentName: string,
    ) => {
      if (!projectId) return
      void confirm({
        title: `Delete "${agentName}"?`,
        icon: <ExclamationCircleOutlined />,
        content:
          'This agent will be permanently removed for all users. This action cannot be undone.',
        okText: 'Delete',
        danger: true,
        onOk: async () => {
          try {
            if (agentType === 'benchmarking') {
              await service.deleteBenchmarkingAgent(projectId, agentUuid)
            } else {
              await service.deleteIssueAgent(projectId, agentUuid)
            }
            queryClient.invalidateQueries([ISSUE_AGENTS_QUERY_KEY])
            queryClient.invalidateQueries(['benchmarking-agents'])
            toast.success('Agent deleted')
          } catch {
            toast.error('Failed to delete agent')
          }
        },
      })
    },
    [confirm, projectId, service, queryClient],
  )

  // Sync void, not async: `confirm` returns as soon as the dialog opens,
  // so an async wrapper would resolve before the user has even seen it. Typed
  // as a promise it invites a caller to await it and believe the delete landed.
  const handleDeleteInsightAgent = useCallback(
    (agent: AgentInsightAgent): void => {
      if (!projectId) return
      void confirm({
        title: `Delete "${agent.name}"?`,
        icon: <ExclamationCircleOutlined />,
        // Deliberately NOT the sibling agents' "permanently removed / cannot be
        // undone" copy. This delete deactivates the agent; the reports it has
        // already produced stay on the feed. Telling a user their insights are
        // about to disappear would stop them deleting an agent that is only
        // going to keep costing money.
        content:
          'This agent will stop running for all users. Reports it has already produced stay on the Insights Feed.',
        okText: 'Delete',
        danger: true,
        onOk: async () => {
          try {
            await insightAgentService.deleteAgent(projectId, agent.uuid)
            // Refetch rather than splice locally: deletion is a soft deactivate
            // the server decides on, and the list query is what knows which
            // agents remain active.
            await refetchInsightAgents()
            toast.success('Agent deleted')
          } catch {
            toast.error('Failed to delete agent')
          }
        },
      })
    },
    [confirm, projectId, insightAgentService, refetchInsightAgents],
  )

  const handleToggleInsightAgentMuted = useCallback(
    async (agent: AgentInsightAgent): Promise<void> => {
      if (!projectId) return
      const nextEnabled = !agent.is_enabled
      try {
        await insightAgentService.setEnabled(projectId, agent.uuid, nextEnabled)
        // Refetch rather than flip locally: the server decides, and a 409 (no
        // grant to write) must not leave the row rendering the state we asked
        // for as though it took.
        await refetchInsightAgents()
        toast.success(
          nextEnabled
            ? 'Agent will appear in your feed'
            : 'Agent hidden from your feed',
        )
      } catch (error) {
        // 409 is not a failure, it is an answer: the caller can see the agent
        // but holds no grant to mute (only an Admin's visibility bypass gets
        // there, and the agents table does not render those rows — measured at
        // 0). Reporting it as "failed" would read as a broken feature for what
        // is really "this one isn't yours to quieten".
        const status = (error as { response?: { status?: number } })?.response
          ?.status
        toast.error(
          status === 409
            ? 'This agent has not been shared with you, so there is nothing to hide'
            : 'Failed to update your feed preference',
        )
      }
    },
    [projectId, insightAgentService, refetchInsightAgents],
  )

  return (
    <AgentListView
      agents={agentsList.agents}
      benchmarkingAgents={benchmarkingAgents}
      insightAgents={insightAgents}
      onSelectInsightAgent={(agent) =>
        projectId &&
        navigate(trendDetectionAgentDetailPath(projectId, agent.uuid))
      }
      onDeleteInsightAgent={handleDeleteInsightAgent}
      onToggleInsightAgentMuted={handleToggleInsightAgentMuted}
      agentsPage={agentsList.agentsPage}
      setAgentsPage={agentsList.setAgentsPage}
      loadingAgents={
        agentsList.loadingAgents ||
        featureFlagsLoading ||
        (hasBenchmarkingFlag && benchmarkingAgentsLoading) ||
        loadingInsightAgents
      }
      agentRunInfo={agentsList.agentRunInfo}
      availableAgentTypes={availableAgentTypes}
      onStartSetup={handleStartSetup}
      onQuickCreate={handleQuickCreate}
      onSelectAgent={(agent) => {
        trackResearchAgents(ResearchAgentsAction.open_agent_config, {
          source: 'agent_list',
          agent_type: agent.agent_type,
          agent_uuid: agent.uuid,
          agent_name: agent.name,
        })
        onSelectAgent(agent.uuid)
      }}
      onEditAgent={
        onEditAgent
          ? (agent) => {
              trackResearchAgents(ResearchAgentsAction.open_agent_config, {
                source: 'agent_list_edit',
                agent_type: agent.agent_type,
                agent_uuid: agent.uuid,
                agent_name: agent.name,
              })
              onEditAgent(agent.uuid)
            }
          : undefined
      }
      onSelectBenchmarkingAgent={(agent) => {
        if (projectId) {
          navigate(trendDetectionBenchmarkingAgentPath(projectId, agent.uuid))
        }
      }}
      onEditBenchmarkingAgent={(agent) => {
        if (projectId) {
          navigate(
            `${trendDetectionBenchmarkingAgentPath(projectId, agent.uuid)}?mode=edit`,
          )
        }
      }}
      onToggleEnabled={handleToggleEnabled}
      onDeleteAgent={handleDeleteAgent}
      onRerun={handleRerun}
      projectId={projectId ?? ''}
      filterOptions={filterOptions}
    />
  )
}
